import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  getAvaialbleFundsForBusiness,
  getUsedFundsForBusiness,
  shareBusiness,
  unshareBusiness,
} from 'lib/api/multiverse/business';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import { getShortString, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BusinessRelation } from '../../index';
interface FundDialogProps {
  open: boolean;
  onClose: () => void;
  business: BusinessRelation;
}

const FundDialog = (props: FundDialogProps) => {
  const { open, onClose, business } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [addingFund, setAddingFund] = useState(false);
  const [availableFunds, setAvailableFunds] = useState<any[]>([]);
  const [selectedFund, setSelectedFund] = useState<any>(null);
  const [expiresAt, setExpiresAt] = useState<any>('invalid date');
  const [usedFunds, setUsedFunds] = useState<any[]>([]);

  const setUp = async () => {
    setLoading(true);
    const businessIdentifier = business.identifier;
    try {
      const usedFunds = await getUsedFundsForBusiness(
        dispatch,
        businessIdentifier,
      );
      setUsedFunds(usedFunds);
    } catch (error) {
      setLoading(false);
      return showAlert(dispatch, 'error', 'Error al obtener los fondos');
    }
    setAddingFund(false);
    setLoading(false);
  };

  const handleAddFund = async () => {
    setLoading(true);
    const businessIdentifier = business.identifier;
    try {
      const availableFunds = await getAvaialbleFundsForBusiness(
        dispatch,
        businessIdentifier,
      );
      if (availableFunds.length === 0) {
        setAddingFund(false);
        setLoading(false);
        return showAlert(dispatch, 'warning', 'No hay fondos disponibles');
      }
      setAvailableFunds(availableFunds);
    } catch (error) {
      setLoading(false);
      return showAlert(dispatch, 'error', 'Error al obtener los fondos');
    }
    setAddingFund(true);
    setLoading(false);
  };

  const handleShareBusiness = async () => {
    setLoading(true);
    const businessIdentifier = business.identifier;
    try {
      await shareBusiness(dispatch, {
        businessIdentifier,
        fundId: selectedFund,
        expiresAt,
      });
      showAlert(dispatch, 'success', 'Fondo habilitado con éxito');
      await setUp();
    } catch (error) {
      setLoading(false);
      return showAlert(dispatch, 'error', 'Error al habilitar el fondo');
    }
    setAddingFund(false);
    setLoading(false);
  };

  const handleUnshareBusiness = async (id: any) => {
    setLoading(true);
    try {
      await unshareBusiness(dispatch, {
        businessIdentifier: business.identifier,
        fundId: id,
      });
      showAlert(dispatch, 'success', 'Fondo deshabilitado con éxito');
      await setUp();
    } catch (error) {
      return showAlert(dispatch, 'error', 'Error al deshabilitar el fondo');
    }
    setLoading(false);
  };
  const actions = [
    {
      action: handleUnshareBusiness,
      actionLabel: 'Deshabilitar',
    },
  ];

  useEffect(() => {
    setUp();
  }, []);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="h5">{`${getShortString(business.businessName)} - ${business.identifier}`}</Typography>
      </DialogTitle>
      <DialogContent>
        {!addingFund && (
          <div
            style={{
              paddingBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">Conexiones actuales</Typography>
            <CustomButton
              color="primary"
              onClick={handleAddFund}
              disabled={loading || addingFund}
            >
              Nueva conexión a fondo
            </CustomButton>
          </div>
        )}
        {loading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: '100px',
            }}
          >
            <CenteredCircularProgress type="dialog" />
          </div>
        )}
        {!addingFund && !loading && (
          <EnhancedTable
            data={usedFunds}
            dataHeaders={[
              {
                label: 'Fondo',
                key: 'name',
              },
              {
                label: 'Rut fondo',
                key: 'identifier',
              },
              {
                label: 'Fecha de vencimiento',
                key: 'expiresAt',
                type: 'date',
              },
            ]}
            actions={actions}
            showActions
          />
        )}
        {addingFund && !loading && (
          <div>
            <FormControl margin="dense" fullWidth>
              <InputLabel id="fund-label">Fondo</InputLabel>
              <Select
                labelId="fund-label"
                fullWidth
                value={selectedFund}
                onChange={(e) => setSelectedFund(e.target.value)}
                label="Fondo"
              >
                {availableFunds.map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {`${x.name} - ${x.taxIdentifier}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fecha de vencimiento"
              value={expiresAt}
              fullWidth
              margin="dense"
              type="date"
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          label="Cancelar"
          onClick={onClose}
          variant="outlined"
          color="secondary"
          disabled={loading}
        >
          Salir
        </CustomButton>
        {addingFund && (
          <CustomButton
            color="primary"
            onClick={handleShareBusiness}
            disabled={
              loading ||
              !selectedFund ||
              !expiresAt ||
              expiresAt === 'invalid date'
            }
          >
            Habilitar
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FundDialog;
